import React from "react";
import type { AuthUser } from "../../../../api/types";
import type { Job } from "../../../../models";
import { GeoJSONDataMap, GroupedGeoJSONFiles } from "../../../../utilities/types";
import type { PropertyWithExamples, FeatureStats } from "../../ImportTable/ImportTable/ImportTable.types";
import type { PropertyMappings } from "../../shared/PropertyMappings.types";

// Re-export props from ImportHelpers for convenience
export type { ImportFormProps as ImportFormGeoDataProps } from "../../shared/ImportHelpers";

// Re-export wizard types
export type { InitialGeoData } from "./wizard/WizardContainer";

// View state for the GeoData import form (upload vs wizard)
export type GeoDataViewState = "upload" | "wizard";

// Re-export commonly used types
export type { GeoFileType, GeoJSONDataMap, GroupedGeoJSONFiles } from "../../../../utilities/types";
export type { PropertyWithExamples, FeatureStats } from "../../ImportTable/ImportTable/ImportTable.types";
export type { PropertyMappings } from "../../shared/PropertyMappings.types";
export type { FileValidationWarning } from "./components/GeoTypeSelectorGroup/GeoTypeSelectorGroup.helpers";

// Unmatched features state structure
export interface UnmatchedFeaturesState {
	centroids: { count: number; filePaths: string[] };
	cores: { count: number; filePaths: string[] };
	zones: { count: number; filePaths: string[] };
}

// Default value for unmatched features
export const DEFAULT_UNMATCHED_FEATURES: UnmatchedFeaturesState = {
	centroids: { count: 0, filePaths: [] },
	cores: { count: 0, filePaths: [] },
	zones: { count: 0, filePaths: [] },
};

// ViewModel interface - everything the View needs
export interface ImportFormGeoDataViewModel {
	// State (read-only from View)
	loading: boolean;
	jobData: Job[];
	selectableGeoData: GroupedGeoJSONFiles;
	originalFiles: File[];
	geoDataMap: GeoJSONDataMap;
	extractedHeaderProperties: PropertyWithExamples[];
	allPropertyMappings: Record<string, PropertyMappings>;
	featureStats: FeatureStats | undefined;
	unmatchedFeatures: UnmatchedFeaturesState;
	fileValidationWarnings: import("./components/GeoTypeSelectorGroup/GeoTypeSelectorGroup.helpers").FileValidationWarning[];

	// View state (upload vs wizard)
	viewState: GeoDataViewState;
	wizardJobs: Job[];
	initialGeoData: import("./wizard/WizardContainer").InitialGeoData;

	// Computed values
	shouldShowExtractButton: boolean;

	// Context
	userContext: AuthUser | undefined;

	// Refs
	importTableRef: React.RefObject<HTMLDivElement | null>;

	// State setters (for child components)
	setGeoDataMap: React.Dispatch<React.SetStateAction<GeoJSONDataMap>>;
	setSelectableGeoData: React.Dispatch<React.SetStateAction<GroupedGeoJSONFiles>>;
	setOriginalFiles: React.Dispatch<React.SetStateAction<File[]>>;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	setJobData: React.Dispatch<React.SetStateAction<Job[]>>;
	setFileValidationWarnings: React.Dispatch<
		React.SetStateAction<
			import("./components/GeoTypeSelectorGroup/GeoTypeSelectorGroup.helpers").FileValidationWarning[]
		>
	>;

	// Event handlers
	handlePropertyMappingsChange: (groupKey: string, mappings: PropertyMappings) => void;
	handleClear: () => void;
	handleResetForm: () => void;
	handleWizardComplete: (jobs: Job[]) => void;
	handleWizardCancel: () => void;
	handleStartOver: () => void;
}
